import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@/lib/toast';
import { useDevice } from '@/hooks/useDevice';
import { APP_VERSION, APP_BUILD_DATE } from '@/constants/version';

/**
 * Footer component for login screens showing version and device identifier.
 * Useful for debugging device-specific issues.
 */
export function LoginFooter() {
  const { device, loading } = useDevice();
  const longPressTimer = useRef<NodeJS.Timeout>();

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      toast(`Build time: ${APP_BUILD_DATE}`, { channel: 'general' });
    }, 500); // 500ms long press
  };

  const clearTimer = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  // Extract short device ID (first 6 chars, uppercase)
  const shortDeviceId = device?.id
    ? device.id.substring(0, 6).toUpperCase()
    : '------';

  return (
    <footer className="fixed bottom-0 left-0 right-0 p-4 text-center text-xs text-muted-foreground bg-background/80 backdrop-blur-sm border-t border-border">
      <div className="flex items-center justify-center gap-3">
        <span
          title={`Build time: ${APP_BUILD_DATE}`}
          className="cursor-help select-none"
          onTouchStart={handleTouchStart}
          onTouchEnd={clearTimer}
          onTouchMove={clearTimer}
          onContextMenu={(e) => {
            // Prevent context menu on long press on mobile
            if (window.matchMedia('(max-width: 768px)').matches) {
              e.preventDefault();
            }
          }}
        >
          {APP_VERSION}
        </span>
        <span className="text-border">•</span>
        <span className="font-mono">
          {loading ? '...' : shortDeviceId}
        </span>
        {device?.name && (
          <>
            <span className="text-border">•</span>
            <span className="truncate max-w-[120px]">{device.name}</span>
          </>
        )}
        <span className="text-border">•</span>
        <Link to="/privacy" className="hover:text-foreground transition-colors">
          Privacybeleid
        </Link>
      </div>
    </footer>
  );
}
